import {
  SwapInterface,
  SwapTokenPair,
  SwapProvider,
  SwapInfo,
  SwapMode,
  SwapStep,
} from '../interfaces/swapInterface'
import { Actor, ActorSubclass, HttpAgent } from '@dfinity/agent'
import { Principal } from '@dfinity/principal'
import { idlFactory as idlSwapFactory } from '../idls/icpswap/swapFactory.did'
import { idlFactory as idlPoolFactory } from '../idls/icpswap/swapPool.did'
import type { PoolData, _SERVICE as SwapFactory } from '../idls/icpswap/swapFactory.did'
import type { _SERVICE as SwapPool } from '../idls/icpswap/swapPool.did'
import { ICToken } from '../interfaces/common'

const SWAP_FACTORY_CANISTER_ID = '4mmnk-kiaaa-aaaag-qbllq-cai'

export interface PoolUnusedBalance {
  balance0: number
  balance1: number
}

interface ICPSwapInterface extends SwapInterface<ICToken> {
  getPoolUnusedBalance: (pool: SwapTokenPair, userPrincipal: string) => Promise<PoolUnusedBalance>
}

class ICPSwap implements ICPSwapInterface {
  public agent: HttpAgent
  public swapFactory: ActorSubclass<SwapFactory>

  constructor(agent: HttpAgent) {
    this.agent = agent
    this.swapFactory = Actor.createActor(idlSwapFactory, {
      canisterId: SWAP_FACTORY_CANISTER_ID,
      agent: this.agent,
    }) as ActorSubclass<SwapFactory>
  }

  parsePool(pool: PoolData): SwapTokenPair {
    const { fee, canisterId, token0, token1 } = pool

    return {
      swapCanisterPrincipal: canisterId.toString(),
      feeRate: Number(fee),
      token0: {
        principal: token0.address,
        standard: token0.standard,
      },
      token1: {
        principal: token1.address,
        standard: token1.standard,
      },
      provider: SwapProvider.ICPSWAP,
      paused: false,
    }
  }

  async getPair(from: ICToken, to: ICToken) {
    const poolResponse = await this.swapFactory.getPool({
      fee: 3000n,
      token0: { address: from.address, standard: from.standard },
      token1: { address: to.address, standard: to.standard },
    })

    if ('ok' in poolResponse) {
      return this.parsePool(poolResponse.ok)
    }

    if ('err' in poolResponse) {
      return Promise.reject(poolResponse.err)
    }

    return Promise.reject('Unknown response')
  }

  getPoolActor(poolCanisterPrincipal: string) {
    return Actor.createActor(idlPoolFactory, {
      canisterId: Principal.fromText(poolCanisterPrincipal),
      agent: this.agent,
    }) as ActorSubclass<SwapPool>
  }

  async getQuote(swapCanisterPrincipal: string, mode: SwapMode, amount: number) {
    const actor = this.getPoolActor(swapCanisterPrincipal)

    const response = await actor.quote({
      amountIn: amount.toString(),
      amountOutMinimum: '0',
      zeroForOne: mode === SwapMode.SELL,
    })

    if ('ok' in response) {
      const info: SwapInfo = {
        quote: Number(response.ok),
      }
      return info
    }

    if ('err' in response) {
      return Promise.reject(response.err)
    }

    return Promise.reject('Unknown error')
  }

  async swap(
    swapCanisterPrincipal: string,
    mode: SwapMode,
    fromToken: ICToken,
    toToken: ICToken,
    amount: number,
    limitPrice: number,
    _maxSlippage: number,
    setStep: (step: SwapStep) => void
  ) {
    const actor = this.getPoolActor(swapCanisterPrincipal)

    const depositMethod = fromToken.standard === 'ICRC1' ? actor.deposit : actor.depositFrom

    setStep(SwapStep.Deposit)

    const depositResponse = await depositMethod({
      fee: BigInt(fromToken.fee || 0),
      token: fromToken.address,
      amount: BigInt(amount),
    })

    if ('err' in depositResponse) {
      return Promise.reject(Object.values(depositResponse.err).join(','))
    }

    if ('ok' in depositResponse) {
      const depositedAmount = Number(depositResponse.ok)

      setStep(SwapStep.Swap)
      const swapResponse = await actor.swap({
        amountIn: `${depositedAmount - (fromToken.fee || 0)}`,
        zeroForOne: mode === SwapMode.SELL,
        amountOutMinimum: limitPrice.toString(),
      })

      if ('err' in swapResponse) {
        if (fromToken.standard === 'ICRC1') {
          setStep(SwapStep.ErrorFallback)

          await actor.withdraw({
            fee: BigInt(fromToken.fee || 0),
            token: fromToken.address,
            amount: BigInt(depositedAmount - (fromToken.fee || 0)),
          })
        }
        return Promise.reject(Object.values(swapResponse.err).join(','))
      }

      if ('ok' in swapResponse) {
        const swappedAmount = Number(swapResponse.ok)

        setStep(SwapStep.Withdraw)

        const withdrawResponse = await actor.withdraw({
          fee: BigInt(toToken.fee || 0),
          token: toToken.address,
          amount: BigInt(swappedAmount - (toToken.fee || 0)),
        })

        if ('ok' in withdrawResponse) {
          return Number(withdrawResponse.ok)
        }

        if ('err' in withdrawResponse) {
          return Promise.reject(Object.values(withdrawResponse.err).join(','))
        }
      }
    }

    return Promise.reject('Unknown status')
  }

  async getPairs() {
    const pools = await this.swapFactory.getPools()

    if ('ok' in pools) {
      return pools.ok.map(this.parsePool.bind(this))
    }

    if ('err' in pools) {
      return Promise.reject(pools.err)
    }

    return []
  }

  async getPoolUnusedBalance(pool: SwapTokenPair, userPrincipal: string) {
    const actor = this.getPoolActor(pool.swapCanisterPrincipal)
    const unusedBalance = await actor.getUserUnusedBalance(Principal.fromText(userPrincipal))

    if ('err' in unusedBalance) {
      return Promise.reject(unusedBalance.err)
    }

    if ('ok' in unusedBalance) {
      return {
        balance0: Number(unusedBalance.ok.balance0),
        balance1: Number(unusedBalance.ok.balance1),
      }
    }

    return Promise.reject('Unknown response')
  }

  getDepositAddress(swapCanisterPrincipal: string, principal: Principal) {
    return Promise.reject('Not implemented')
  }

  getSwapPairEvents(swapPairCanisterPrincipal: string, accountPrincipal: string) {
    return Promise.reject('Not implemented')
  }

  fallback(swapCanisterPrincipal: string, subaccount: number[]) {
    return Promise.reject('Not implemented')
  }
}

export default ICPSwap
